/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  DeleteSourcePathParameters,
  TLCreateFoundrySourceRequest,
  TLFoundrySourceResponse,
  TLFoundrySourceTablePage,
  TLHTTPValidationError,
  TLListAvailableSourceTablesRequest,
  TLListFoundrySourcesResponse,
  TLUpdateFoundrySourceRequest,
  UpdateSourceCredentialsPathParameters,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary List Sources
 */
export const listSources = (options?: SecondParameter<typeof apiInstance>, signal?: AbortSignal) => {
  return apiInstance<TLListFoundrySourcesResponse>({ url: `/foundry/sources`, method: 'GET', signal }, options);
};

export const getListSourcesQueryKey = () => {
  return ['foundry', 'sources'] as const;
};

export const getListSourcesQueryOptions = <
  TData = Awaited<ReturnType<typeof listSources>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSources>>, TError, TData>>;
  request?: SecondParameter<typeof apiInstance>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListSourcesQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listSources>>> = ({ signal }) =>
    listSources(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listSources>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListSourcesQueryResult = NonNullable<Awaited<ReturnType<typeof listSources>>>;
export type ListSourcesQueryError = ErrorType<unknown>;

export function useListSources<TData = Awaited<ReturnType<typeof listSources>>, TError = ErrorType<unknown>>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSources>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listSources>>,
          TError,
          Awaited<ReturnType<typeof listSources>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListSources<TData = Awaited<ReturnType<typeof listSources>>, TError = ErrorType<unknown>>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSources>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listSources>>,
          TError,
          Awaited<ReturnType<typeof listSources>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListSources<TData = Awaited<ReturnType<typeof listSources>>, TError = ErrorType<unknown>>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSources>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Sources
 */

export function useListSources<TData = Awaited<ReturnType<typeof listSources>>, TError = ErrorType<unknown>>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSources>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListSourcesQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List Sources
 */
export const prefetchListSourcesQuery = async <
  TData = Awaited<ReturnType<typeof listSources>>,
  TError = ErrorType<unknown>,
>(
  queryClient: QueryClient,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listSources>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListSourcesQueryOptions(options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List Sources
 */
export const invalidateListSources = async (
  queryClient: QueryClient,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getListSourcesQueryKey() }, options);

  return queryClient;
};

/**
 * @summary Create Source
 */
export const createSource = (
  tLCreateFoundrySourceRequest: BodyType<TLCreateFoundrySourceRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLFoundrySourceResponse>(
    {
      url: `/foundry/sources`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLCreateFoundrySourceRequest,
      signal,
    },
    options,
  );
};

export const getCreateSourceQueryKey = (tLCreateFoundrySourceRequest?: BodyType<TLCreateFoundrySourceRequest>) => {
  return ['POST', 'foundry', 'sources', tLCreateFoundrySourceRequest] as const;
};

export const getCreateSourceQueryOptions = <
  TData = Awaited<ReturnType<typeof createSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLCreateFoundrySourceRequest: BodyType<TLCreateFoundrySourceRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getCreateSourceQueryKey(tLCreateFoundrySourceRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof createSource>>> = ({ signal }) =>
    createSource(tLCreateFoundrySourceRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof createSource>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type CreateSourceQueryResult = NonNullable<Awaited<ReturnType<typeof createSource>>>;
export type CreateSourceQueryError = ErrorType<TLHTTPValidationError>;

export function useCreateSource<
  TData = Awaited<ReturnType<typeof createSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLCreateFoundrySourceRequest: BodyType<TLCreateFoundrySourceRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof createSource>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof createSource>>,
          TError,
          Awaited<ReturnType<typeof createSource>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateSource<
  TData = Awaited<ReturnType<typeof createSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLCreateFoundrySourceRequest: BodyType<TLCreateFoundrySourceRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createSource>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof createSource>>,
          TError,
          Awaited<ReturnType<typeof createSource>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateSource<
  TData = Awaited<ReturnType<typeof createSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLCreateFoundrySourceRequest: BodyType<TLCreateFoundrySourceRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create Source
 */

export function useCreateSource<
  TData = Awaited<ReturnType<typeof createSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLCreateFoundrySourceRequest: BodyType<TLCreateFoundrySourceRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCreateSourceQueryOptions(tLCreateFoundrySourceRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create Source
 */
export const prefetchCreateSourceQuery = async <
  TData = Awaited<ReturnType<typeof createSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLCreateFoundrySourceRequest: BodyType<TLCreateFoundrySourceRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCreateSourceQueryOptions(tLCreateFoundrySourceRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Create Source
 */
export const invalidateCreateSource = async (
  queryClient: QueryClient,
  tLCreateFoundrySourceRequest: BodyType<TLCreateFoundrySourceRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getCreateSourceQueryKey(tLCreateFoundrySourceRequest) }, options);

  return queryClient;
};

/**
 * @summary List Available Source Tables
 */
export const listAvailableSourceTables = (
  tLListAvailableSourceTablesRequest: BodyType<TLListAvailableSourceTablesRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLFoundrySourceTablePage>(
    {
      url: `/foundry/sources/available-tables`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLListAvailableSourceTablesRequest,
      signal,
    },
    options,
  );
};

export const getListAvailableSourceTablesQueryKey = (
  tLListAvailableSourceTablesRequest?: BodyType<TLListAvailableSourceTablesRequest>,
) => {
  return ['POST', 'foundry', 'sources', 'available-tables', tLListAvailableSourceTablesRequest] as const;
};

export const getListAvailableSourceTablesQueryOptions = <
  TData = Awaited<ReturnType<typeof listAvailableSourceTables>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLListAvailableSourceTablesRequest: BodyType<TLListAvailableSourceTablesRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listAvailableSourceTables>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListAvailableSourceTablesQueryKey(tLListAvailableSourceTablesRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listAvailableSourceTables>>> = ({ signal }) =>
    listAvailableSourceTables(tLListAvailableSourceTablesRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listAvailableSourceTables>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListAvailableSourceTablesQueryResult = NonNullable<Awaited<ReturnType<typeof listAvailableSourceTables>>>;
export type ListAvailableSourceTablesQueryError = ErrorType<TLHTTPValidationError>;

export function useListAvailableSourceTables<
  TData = Awaited<ReturnType<typeof listAvailableSourceTables>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLListAvailableSourceTablesRequest: BodyType<TLListAvailableSourceTablesRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listAvailableSourceTables>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listAvailableSourceTables>>,
          TError,
          Awaited<ReturnType<typeof listAvailableSourceTables>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListAvailableSourceTables<
  TData = Awaited<ReturnType<typeof listAvailableSourceTables>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLListAvailableSourceTablesRequest: BodyType<TLListAvailableSourceTablesRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listAvailableSourceTables>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listAvailableSourceTables>>,
          TError,
          Awaited<ReturnType<typeof listAvailableSourceTables>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListAvailableSourceTables<
  TData = Awaited<ReturnType<typeof listAvailableSourceTables>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLListAvailableSourceTablesRequest: BodyType<TLListAvailableSourceTablesRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listAvailableSourceTables>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Available Source Tables
 */

export function useListAvailableSourceTables<
  TData = Awaited<ReturnType<typeof listAvailableSourceTables>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLListAvailableSourceTablesRequest: BodyType<TLListAvailableSourceTablesRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listAvailableSourceTables>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListAvailableSourceTablesQueryOptions(tLListAvailableSourceTablesRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List Available Source Tables
 */
export const prefetchListAvailableSourceTablesQuery = async <
  TData = Awaited<ReturnType<typeof listAvailableSourceTables>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLListAvailableSourceTablesRequest: BodyType<TLListAvailableSourceTablesRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listAvailableSourceTables>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListAvailableSourceTablesQueryOptions(tLListAvailableSourceTablesRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List Available Source Tables
 */
export const invalidateListAvailableSourceTables = async (
  queryClient: QueryClient,
  tLListAvailableSourceTablesRequest: BodyType<TLListAvailableSourceTablesRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getListAvailableSourceTablesQueryKey(tLListAvailableSourceTablesRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Delete Source
 */
export const deleteSource = (
  { catalogName }: DeleteSourcePathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<void>({ url: `/foundry/sources/${catalogName}`, method: 'DELETE', signal }, options);
};

export const getDeleteSourceQueryKey = ({ catalogName }: DeleteSourcePathParameters) => {
  return ['DELETE', 'foundry', 'sources', catalogName] as const;
};

export const getDeleteSourceQueryOptions = <
  TData = Awaited<ReturnType<typeof deleteSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { catalogName }: DeleteSourcePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getDeleteSourceQueryKey({ catalogName });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof deleteSource>>> = ({ signal }) =>
    deleteSource({ catalogName }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: catalogName !== null && catalogName !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof deleteSource>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type DeleteSourceQueryResult = NonNullable<Awaited<ReturnType<typeof deleteSource>>>;
export type DeleteSourceQueryError = ErrorType<TLHTTPValidationError>;

export function useDeleteSource<
  TData = Awaited<ReturnType<typeof deleteSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteSourcePathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteSource>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteSource>>,
          TError,
          Awaited<ReturnType<typeof deleteSource>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteSource<
  TData = Awaited<ReturnType<typeof deleteSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteSourcePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteSource>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteSource>>,
          TError,
          Awaited<ReturnType<typeof deleteSource>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteSource<
  TData = Awaited<ReturnType<typeof deleteSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteSourcePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Delete Source
 */

export function useDeleteSource<
  TData = Awaited<ReturnType<typeof deleteSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { catalogName }: DeleteSourcePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getDeleteSourceQueryOptions({ catalogName }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Delete Source
 */
export const prefetchDeleteSourceQuery = async <
  TData = Awaited<ReturnType<typeof deleteSource>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { catalogName }: DeleteSourcePathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteSource>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getDeleteSourceQueryOptions({ catalogName }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Delete Source
 */
export const invalidateDeleteSource = async (
  queryClient: QueryClient,
  { catalogName }: DeleteSourcePathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getDeleteSourceQueryKey({ catalogName }) }, options);

  return queryClient;
};

/**
 * @summary Update Source Credentials
 */
export const updateSourceCredentials = (
  { catalogName }: UpdateSourceCredentialsPathParameters,
  tLUpdateFoundrySourceRequest: BodyType<TLUpdateFoundrySourceRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLFoundrySourceResponse>(
    {
      url: `/foundry/sources/${catalogName}`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: tLUpdateFoundrySourceRequest,
      signal,
    },
    options,
  );
};

export const getUpdateSourceCredentialsQueryKey = (
  { catalogName }: UpdateSourceCredentialsPathParameters,
  tLUpdateFoundrySourceRequest?: BodyType<TLUpdateFoundrySourceRequest>,
) => {
  return ['PATCH', 'foundry', 'sources', catalogName, tLUpdateFoundrySourceRequest] as const;
};

export const getUpdateSourceCredentialsQueryOptions = <
  TData = Awaited<ReturnType<typeof updateSourceCredentials>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { catalogName }: UpdateSourceCredentialsPathParameters,
  tLUpdateFoundrySourceRequest: BodyType<TLUpdateFoundrySourceRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateSourceCredentials>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getUpdateSourceCredentialsQueryKey({ catalogName }, tLUpdateFoundrySourceRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof updateSourceCredentials>>> = ({ signal }) =>
    updateSourceCredentials({ catalogName }, tLUpdateFoundrySourceRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: catalogName !== null && catalogName !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof updateSourceCredentials>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type UpdateSourceCredentialsQueryResult = NonNullable<Awaited<ReturnType<typeof updateSourceCredentials>>>;
export type UpdateSourceCredentialsQueryError = ErrorType<TLHTTPValidationError>;

export function useUpdateSourceCredentials<
  TData = Awaited<ReturnType<typeof updateSourceCredentials>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateSourceCredentialsPathParameters,
  tLUpdateFoundrySourceRequest: BodyType<TLUpdateFoundrySourceRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateSourceCredentials>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateSourceCredentials>>,
          TError,
          Awaited<ReturnType<typeof updateSourceCredentials>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateSourceCredentials<
  TData = Awaited<ReturnType<typeof updateSourceCredentials>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateSourceCredentialsPathParameters,
  tLUpdateFoundrySourceRequest: BodyType<TLUpdateFoundrySourceRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateSourceCredentials>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateSourceCredentials>>,
          TError,
          Awaited<ReturnType<typeof updateSourceCredentials>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateSourceCredentials<
  TData = Awaited<ReturnType<typeof updateSourceCredentials>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateSourceCredentialsPathParameters,
  tLUpdateFoundrySourceRequest: BodyType<TLUpdateFoundrySourceRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateSourceCredentials>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update Source Credentials
 */

export function useUpdateSourceCredentials<
  TData = Awaited<ReturnType<typeof updateSourceCredentials>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { catalogName }: UpdateSourceCredentialsPathParameters,
  tLUpdateFoundrySourceRequest: BodyType<TLUpdateFoundrySourceRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateSourceCredentials>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUpdateSourceCredentialsQueryOptions({ catalogName }, tLUpdateFoundrySourceRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Update Source Credentials
 */
export const prefetchUpdateSourceCredentialsQuery = async <
  TData = Awaited<ReturnType<typeof updateSourceCredentials>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { catalogName }: UpdateSourceCredentialsPathParameters,
  tLUpdateFoundrySourceRequest: BodyType<TLUpdateFoundrySourceRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateSourceCredentials>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUpdateSourceCredentialsQueryOptions({ catalogName }, tLUpdateFoundrySourceRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Update Source Credentials
 */
export const invalidateUpdateSourceCredentials = async (
  queryClient: QueryClient,
  { catalogName }: UpdateSourceCredentialsPathParameters,
  tLUpdateFoundrySourceRequest: BodyType<TLUpdateFoundrySourceRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getUpdateSourceCredentialsQueryKey({ catalogName }, tLUpdateFoundrySourceRequest) },
    options,
  );

  return queryClient;
};
